import type {
  HouseholdCategory,
  HouseholdItem,
  PaperKind,
  Recurrence,
} from './types';
import { deriveStatus } from './status';

type LooseRecord = Record<string, unknown>;

const RENEWAL_HINTS = [
  'insurance',
  'registration',
  'rego',
  'licence',
  'license',
  'membership',
  'subscription',
  'renewal',
  'warranty',
  'contract',
];

const BILL_HINTS = [
  'electric',
  'energy',
  'gas',
  'water',
  'internet',
  'phone',
  'utility',
  'rent',
  'mortgage',
  'council',
  'rates',
  'school fee',
];

const CAR_HINTS = ['car', 'rego', 'registration', 'vehicle', 'motoring', 'nrma', 'racv'];
const HOUSE_HINTS = ['rent', 'mortgage', 'home', 'house', 'council', 'rates', 'electric', 'gas', 'water'];
const GIFT_HINTS = ['birthday', 'gift', 'anniversary'];
const PASSPORT_HINTS = ['travel', 'flight', 'holiday', 'passport', 'trip'];

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function asDateString(value: unknown, fallback?: string): string {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string' && value) return value;
  return fallback || new Date().toISOString();
}

function includesAny(haystack: string, needles: string[]): boolean {
  const h = haystack.toLowerCase();
  return needles.some((n) => h.includes(n));
}

function mapFrequency(raw?: string): Recurrence | undefined {
  const f = (raw || '').toLowerCase();
  if (['weekly', 'week'].includes(f)) return 'weekly';
  if (['biweekly', 'fortnightly', 'bi-weekly'].includes(f)) return 'biweekly';
  if (['monthly', 'month'].includes(f)) return 'monthly';
  if (['quarterly', 'quarter'].includes(f)) return 'quarterly';
  if (['yearly', 'annual', 'annually', 'year'].includes(f)) return 'annual';
  return undefined;
}

export function classifyCategory(name: string, categoryHint?: string): HouseholdCategory {
  const blob = `${name} ${categoryHint || ''}`;
  if (includesAny(blob, RENEWAL_HINTS)) return 'renewal';
  if (includesAny(blob, BILL_HINTS)) return 'bill';
  if (includesAny(blob, GIFT_HINTS) || includesAny(blob, PASSPORT_HINTS)) return 'event';
  return 'bill';
}

export function pickPaperKind(title: string, category: HouseholdCategory): PaperKind {
  const blob = title.toLowerCase();
  if (includesAny(blob, CAR_HINTS)) return 'car';
  if (includesAny(blob, PASSPORT_HINTS)) return 'passport';
  if (includesAny(blob, GIFT_HINTS)) return 'gift';
  if (includesAny(blob, HOUSE_HINTS)) return 'house';
  if (category === 'renewal') return 'document';
  if (category === 'event') return 'calendar';
  if (category === 'reminder') return 'gift';
  return 'envelope';
}

export function mapBill(raw: LooseRecord): HouseholdItem {
  const title = asString(raw.name, 'Household bill');
  const dueAt = asDateString(raw.dueDate || raw.nextPaymentDate);
  const category = classifyCategory(title, asString(raw.category));
  const amount = asNumber(raw.amount);
  const status = deriveStatus(dueAt, asString(raw.status));
  const anomaly = raw.anomalyDetection as LooseRecord | undefined;
  const amountChange = anomaly?.amountChange as LooseRecord | undefined;

  return {
    id: `bill-${asString(raw.id, title)}`,
    title,
    category,
    status,
    dueAt,
    amount,
    currency: asString(raw.currency, 'AUD'),
    recurrence: mapFrequency(asString(raw.frequency)),
    source: asString(raw.source) === 'AUTO_DISCOVERED' || asString(raw.source) === 'CONNECTED_ACCOUNT'
      ? 'bank_detected'
      : 'manual',
    paperKind: pickPaperKind(title, category),
    rawSourceId: asString(raw.id),
    detection: {
      summary:
        asString(raw.source) === 'AUTO_DISCOVERED' || asString(raw.source) === 'CONNECTED_ACCOUNT'
          ? 'PIAI detected this from account activity.'
          : 'Tracked as a household bill.',
      lastPaidAt: asString(raw.lastPaidDate) || undefined,
      lastPaidAmount: asNumber(raw.lastPaidAmount),
      expectedRecurrence: mapFrequency(asString(raw.frequency)),
      foundIn:
        asString(raw.source) === 'AUTO_DISCOVERED' || asString(raw.source) === 'CONNECTED_ACCOUNT'
          ? 'Found in connected account activity'
          : undefined,
      confidence:
        asString(raw.source) === 'AUTO_DISCOVERED' || asString(raw.source) === 'CONNECTED_ACCOUNT'
          ? asNumber(raw.confidenceScore) ?? 0.85
          : 0.7,
      anomaly:
        amountChange?.isAnomaly
          ? {
              kind: 'amount_increase',
              detail: `Amount changed by ${asNumber(amountChange.percentage) ?? 0}% from previous payment.`,
            }
          : undefined,
    },
  };
}

export function mapRecurring(raw: LooseRecord): HouseholdItem {
  const title = asString(raw.templateName || raw.description, 'Recurring payment');
  const dueAt = asDateString(raw.nextDueDate || raw.startDate);
  const category = classifyCategory(title, asString(raw.type));
  const amount = asNumber(raw.amount);
  const status = deriveStatus(dueAt, raw.isActive === false ? 'done' : null);

  return {
    id: `recurring-${asString(raw.id, title)}`,
    title,
    category: category === 'bill' && includesAny(title, RENEWAL_HINTS) ? 'renewal' : category,
    status,
    dueAt,
    amount,
    currency: asString(raw.currency, 'AUD'),
    recurrence: mapFrequency(asString(raw.frequency)),
    source: 'recurring',
    paperKind: pickPaperKind(title, category),
    rawSourceId: asString(raw.id),
    notes: asString(raw.notes) || undefined,
    detection: {
      summary: 'PIAI predicted the next occurrence from recurring payment patterns.',
      lastPaidAt: asString(raw.lastExecutedDate) || undefined,
      expectedRecurrence: mapFrequency(asString(raw.frequency)),
      foundIn: 'Predicted from recurring payment pattern',
      confidence: 0.88,
    },
  };
}

export function mapReminder(raw: LooseRecord): HouseholdItem {
  const title = asString(raw.billName || raw.title, 'Reminder');
  const dueAt = asDateString(raw.dueDate || raw.reminderDate);
  const amount = asNumber(raw.amount);
  const meta = (raw.metadata || {}) as LooseRecord;
  const categoryHint = asString(meta.category);
  const category =
    categoryHint === 'bill' ||
    categoryHint === 'renewal' ||
    categoryHint === 'event' ||
    categoryHint === 'reminder'
      ? categoryHint
      : 'reminder';
  const statusRaw = asString(raw.status);
  const status =
    statusRaw === 'paid' || statusRaw === 'done'
      ? 'done'
      : statusRaw === 'reminded'
        ? 'needs_attention'
        : deriveStatus(dueAt, statusRaw);
  const recurrence = mapFrequency(asString(meta.recurrence));
  const isManual = meta.piai === true;

  return {
    id: `reminder-${asString(raw.id, title)}`,
    title,
    category,
    status,
    dueAt,
    amount,
    currency: asString(raw.currency, 'AUD'),
    recurrence,
    source: isManual ? 'manual' : 'reminder',
    paperKind: pickPaperKind(title, category),
    rawSourceId: asString(raw.id),
    notes: asString(raw.notes) || undefined,
    detection: {
      summary: isManual
        ? 'Added by you for this household.'
        : 'PIAI is monitoring this reminder for your household.',
      expectedRecurrence: recurrence,
      confidence: isManual ? 1 : 0.8,
      foundIn: isManual ? 'Manual entry' : undefined,
    },
  };
}

export function mergeHouseholdItems(parts: HouseholdItem[][]): HouseholdItem[] {
  const seen = new Set<string>();
  const merged: HouseholdItem[] = [];
  for (const group of parts) {
    for (const item of group) {
      const key = `${item.title.toLowerCase()}|${item.dueAt.slice(0, 10)}|${item.category}`;
      if (seen.has(key) || seen.has(item.id)) continue;
      seen.add(key);
      seen.add(item.id);
      merged.push(item);
    }
  }
  return merged.sort(
    (a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
  );
}
